// ─────────────────────────────────────────────
//  testRoster — who was actually working on a given facility-day.
//
//  Pure. This is the list the testing-mode picker offers, and it is the whole
//  point of the feature: the owner asked to sign in "as the user they were in
//  the roster", not as whoever happens to be in the directory today.
//
//  ⚠️ NOT `listFacilityStaff`. The existing view-as picker reads the CURRENT
//  directory of the admin's OWN facility. For a past day that is the wrong
//  question twice over: people join and leave, and the point is to stand in a
//  specific day's shoes.
//
//  ⚠️ AN ID IS REQUIRED, A NAME IS NOT. `setViewAs` needs a uid; a row that
//  names a person with no account (a locum typed into the sheet, an unresolved
//  import) cannot be impersonated and is EXCLUDED rather than offered as a
//  control that would silently do nothing. A missing id means "unresolved",
//  never "absent".
// ─────────────────────────────────────────────

const clean = (s) => (typeof s === "string" ? s.trim() : "");

// Add a candidate, keeping the first non-empty name we saw for that uid and
// the strongest role. One person appearing as both AIC and a room's attending
// is ONE entry — a duplicated row is a picker that looks broken.
function add(map, uid, name, role) {
  const id = clean(uid);
  if (!id) return; // unresolved — cannot be impersonated
  const prev = map.get(id);
  if (!prev) {
    map.set(id, { uid: id, name: clean(name), roles: new Set([role]) });
    return;
  }
  if (!prev.name && clean(name)) prev.name = clean(name);
  prev.roles.add(role);
}

/**
 * @param assignments the day's rows (ahpId/ahpName, attendingId/attendingName)
 * @param board       the daily_boards doc (aicId / aicName)
 * @param staff       optional, to fill in a name an id alone cannot supply
 * @returns [{ uid, name, role }] sorted by name, then uid
 */
export function dayRosterFrom({ assignments = [], board = null, staff = [] } = {}) {
  const map = new Map();

  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a) continue;
    add(map, a.ahpId, a.ahpName, "provider");
    add(map, a.attendingId, a.attendingName, "attending");
  }
  if (board) add(map, board.aicId, board.aicName, "aic");

  // A uid with no name anywhere reads as a raw id in the picker, which is
  // unusable. `staff` is the PEOPLE directory (never `users`, which is the auth
  // record and carries push tokens), so it is the right place to look one up.
  const staffById = new Map(
    (Array.isArray(staff) ? staff : [])
      .filter((s) => s && s.id)
      .map((s) => [s.id, clean(s.name)])
  );

  return [...map.values()]
    .map((p) => ({
      uid: p.uid,
      name: p.name || staffById.get(p.uid) || "",
      // The strongest role decides which screens they land on: an AIC outranks
      // an attending, which outranks a provider.
      role: p.roles.has("aic") ? "aic" : p.roles.has("attending") ? "attending" : "provider",
    }))
    .sort((a, b) => (a.name || a.uid).localeCompare(b.name || b.uid));
}

// A day with no impersonable people cannot be tested as anybody, so the screen
// must say so BEFORE capturing rather than presenting an empty picker.
export const hasTestableRoster = (roster) => Array.isArray(roster) && roster.length > 0;

/**
 * The viewAs target AuthContext expects, built from a roster entry.
 *
 * ⚠️ THE ROSTER'S ROLE VOCABULARY IS NOT THE APP'S. This module says
 * aic / attending / provider, because that is what a day's board tells you.
 * AuthContext and config/roles.js speak "anesthesiologist" and "provider", with
 * AIC as a FLAG rather than a role. Handing the roster's words straight to
 * setViewAs would produce an account with a role nothing recognises, and
 * accessLevelOf would silently fall through to the lowest tier.
 */
export function viewAsProfileFor(person) {
  if (!person?.uid) return null;
  const isAIC = person.role === "aic";
  return {
    uid: person.uid,
    name: person.name || "",
    role: isAIC || person.role === "attending" ? "anesthesiologist" : "provider",
    isAIC,
    subRole: null,
    currentRoomId: null,
    currentRoomName: null,
  };
}
